<div class="domain-container">

  <!-- Header -->
  <div class="domain-header">
    <div class="backButton" (click)="goBack()">
      <img src="/assets/icons/arrow-left.svg" alt="Go back" />
    </div>
    <p class="page-title">{{ 'Domain Settings' | translate }}</p>
    <p class="page-subtitle">{{ 'Manage your website domain and subdomain' | translate }}</p>
  </div>

  <!-- Current Website URL Banner -->
  <div class="url-banner" *ngIf="domainSettings.subdomain || domainSettings.custom_domain">
    <div class="url-banner-content">
      <span class="url-label">{{ 'Your Website URL' | translate }}</span>
      <div class="url-row">
        <a [href]="getCurrentWebsiteUrl()" target="_blank" class="website-url">
          {{ getCurrentWebsiteUrl() }}
        </a>
        <button class="url-open-btn" (click)="previewWebsite()">
          <img src="assets/icons/External_link2.svg" alt="open" />
        </button>
      </div>
    </div>
    <div class="url-globe">
      <img src="assets/icons/www.svg" alt="" />
    </div>
  </div>

  <!-- ─── Subdomain Section ─── -->
  <div class="domain-section">
    <div class="section-header">
      <div class="section-title-group">
        <p>{{ 'Subdomain' | translate }}</p>
        <p class="section-desc">{{ 'Free subdomain for your website' | translate }}</p>
      </div>
      <span class="badge badge-free">{{ 'Free' | translate }}</span>
    </div>

    <div class="section-body">
      <form [formGroup]="subdomainForm">

        <div class="field-label">{{ 'Subdomain' | translate }}</div>

        <div class="input-wrapper"
             [class.input-has-error]="subdomainForm.get('subdomain')?.invalid && subdomainForm.get('subdomain')?.touched">
          <input
            class="genie-input-field"
            formControlName="subdomain"
            placeholder="storename"
            (input)="validateSubdomainFormat(subdomainForm.value.subdomain)"
          />
          <div class="input-status-icons">
            <div class="spinner spinner-primary" *ngIf="checkingAvailability"></div>
            <!-- Available -->
            <svg *ngIf="!checkingAvailability && subdomainAvailable === true" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="#22C55E" stroke-width="1.2"/>
              <path d="M5.5 8L7 9.5L10.5 6" stroke="#22C55E" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <!-- Unavailable -->
            <svg *ngIf="!checkingAvailability && subdomainAvailable === false" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="#EF4444" stroke-width="1.2"/>
              <path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="#EF4444" stroke-width="1.3" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="input-suffix">.{{ sitesDomain }}</div>
        </div>

        <!-- Hint + Active badge -->
        <div class="hint-row" *ngIf="domainSettings.subdomain_full">
          <span class="hint-text">{{ domainSettings.subdomain_full }}</span>
          <span class="badge-active" *ngIf="!domainSettings.custom_domain_verified">
            {{ 'Active' | translate }}
          </span>
        </div>

        <!-- Format validation errors -->
        <div class="validation-errors" *ngIf="subdomainErrors.length > 0">
          <div *ngFor="let error of subdomainErrors" class="validation-error">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="5" stroke="#EF4444" stroke-width="1.1"/>
              <path d="M6 3.5V6.5" stroke="#EF4444" stroke-width="1.1" stroke-linecap="round"/>
              <circle cx="6" cy="8.5" r="0.5" fill="#EF4444"/>
            </svg>
            {{ error }}
          </div>
        </div>

        <!-- Angular form validation errors -->
        <div class="validation-errors"
             *ngIf="subdomainForm.get('subdomain')?.invalid && subdomainForm.get('subdomain')?.touched && subdomainErrors.length === 0">
          <div class="validation-error" *ngIf="subdomainForm.get('subdomain')?.hasError('required')">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#EF4444" stroke-width="1.1"/><path d="M6 3.5V6.5" stroke="#EF4444" stroke-width="1.1" stroke-linecap="round"/><circle cx="6" cy="8.5" r="0.5" fill="#EF4444"/></svg>
            {{ 'Subdomain is required' | translate }}
          </div>
          <div class="validation-error" *ngIf="subdomainForm.get('subdomain')?.hasError('pattern')">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#EF4444" stroke-width="1.1"/><path d="M6 3.5V6.5" stroke="#EF4444" stroke-width="1.1" stroke-linecap="round"/><circle cx="6" cy="8.5" r="0.5" fill="#EF4444"/></svg>
            {{ 'Only lowercase letters, numbers, and hyphens allowed' | translate }}
          </div>
          <div class="validation-error" *ngIf="subdomainForm.get('subdomain')?.hasError('minlength')">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#EF4444" stroke-width="1.1"/><path d="M6 3.5V6.5" stroke="#EF4444" stroke-width="1.1" stroke-linecap="round"/><circle cx="6" cy="8.5" r="0.5" fill="#EF4444"/></svg>
            {{ 'Minimum 3 characters required' | translate }}
          </div>
          <div class="validation-error" *ngIf="subdomainForm.get('subdomain')?.hasError('maxlength')">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#EF4444" stroke-width="1.1"/><path d="M6 3.5V6.5" stroke="#EF4444" stroke-width="1.1" stroke-linecap="round"/><circle cx="6" cy="8.5" r="0.5" fill="#EF4444"/></svg>
            {{ 'Maximum 63 characters allowed' | translate }}
          </div>
        </div>

        <!-- Availability status messages
        <div class="status-message status-success"
             *ngIf="!checkingAvailability && subdomainAvailable === true">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5.75" stroke="#16A34A" stroke-width="1.1"/>
            <path d="M4.5 7L6 8.5L9.5 5" stroke="#16A34A" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ 'Subdomain is available!' | translate }}
        </div> -->

        <div class="status-message status-error"
             *ngIf="!checkingAvailability && subdomainAvailable === false">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5.75" stroke="#DC2626" stroke-width="1.1"/>
            <path d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5" stroke="#DC2626" stroke-width="1.2" stroke-linecap="round"/>
          </svg>
          {{ 'Subdomain is already taken' | translate }}
          <button *ngIf="suggestedSubdomain" class="suggestion-btn" (click)="useSuggestion()">
            {{ 'Use' | translate }}: {{ suggestedSubdomain }}
          </button>
        </div>

        <!-- Actions -->
        <div class="section-actions">
          <button
            class="btn-primary"
            (click)="saveSubdomain()"
            [disabled]="subdomainForm.invalid || subdomainAvailable !== true || savingSubdomain || subdomainForm.value.subdomain === savedSubdomain">
            <div class="spinner spinner-white" *ngIf="savingSubdomain"></div>
            <span>{{ savingSubdomain ? ('Saving...' | translate) : ('Save Subdomain' | translate) }}</span>
          </button>
        </div>

      </form>
    </div>
  </div>

  <!-- ─── Custom Domain Section ─── -->
  <div class="domain-section">
    <div class="section-header">
      <div class="section-title-group">
        <p>{{ 'Custom Domain' | translate }}</p>
        <p class="section-desc">{{ 'Use your own domain name' | translate }}</p>
      </div>
      <span class="badge badge-premium">{{ 'Premium' | translate }}</span>
    </div>

    <div class="section-body">
      <form [formGroup]="customDomainForm" [class.form-disabled]="!canUseCustomDomain">

        <div class="field-label">{{ 'Custom Domain' | translate }}</div>

        <div class="input-wrapper"
             [class.input-has-error]="customDomainForm.get('domain')?.invalid && customDomainForm.get('domain')?.touched">
          <input
            class="genie-input-field"
            formControlName="domain"
            placeholder="mystore.com"
            (input)="onDomainInput()"
          />
          <div class="input-status-icons">
            <!-- Verified badge -->
            <svg *ngIf="customDomainVerified" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1.5L9.618 3.018L11.93 2.57L12.5 4.836L14.5 6L13.382 8L14.5 10L12.5 11.164L11.93 13.43L9.618 12.982L8 14.5L6.382 12.982L4.07 13.43L3.5 11.164L1.5 10L2.618 8L1.5 6L3.5 4.836L4.07 2.57L6.382 3.018L8 1.5Z" stroke="#3B82F6" stroke-width="1.1" stroke-linejoin="round"/>
              <path d="M5.5 8L7 9.5L10.5 6" stroke="#3B82F6" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>

        <!-- Hint + Active badge -->
        <div class="hint-row" *ngIf="domainSettings.custom_domain">
          <span class="hint-text">{{ domainSettings.custom_domain }}</span>
          <span class="badge-active" *ngIf="domainSettings.custom_domain_verified">
            {{ 'Active' | translate }}
          </span>
        </div>

        <!-- Upgrade Banner (non-premium) -->
        <div class="upgrade-banner" *ngIf="!canUseCustomDomain">
          <div class="upgrade-icon">
            <img src="assets/icons/crown.svg" alt="crown" />
          </div>
          <div class="upgrade-text">
            <strong>{{ 'Custom domains are available for Website Package subscribers' | translate }}</strong>
            <p>{{ 'Unlock professional branding and enhance your credibility with a custom domain.' | translate }}</p>
          </div>
          <button class="btn-upgrade" (click)="showUpgradeDialog()">
            {{ 'Upgrade Now' | translate }}
          </button>
        </div>

        <!-- Validation errors -->
        <div class="validation-errors" *ngIf="customDomainErrors.length > 0">
          <div *ngFor="let error of customDomainErrors" class="validation-error">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#EF4444" stroke-width="1.1"/><path d="M6 3.5V6.5" stroke="#EF4444" stroke-width="1.1" stroke-linecap="round"/><circle cx="6" cy="8.5" r="0.5" fill="#EF4444"/></svg>
            {{ error }}
          </div>
        </div>

        <div class="validation-errors"
             *ngIf="customDomainForm.get('domain')?.invalid && customDomainForm.get('domain')?.touched && customDomainErrors.length === 0">
          <div class="validation-error" *ngIf="customDomainForm.get('domain')?.hasError('required')">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#EF4444" stroke-width="1.1"/><path d="M6 3.5V6.5" stroke="#EF4444" stroke-width="1.1" stroke-linecap="round"/><circle cx="6" cy="8.5" r="0.5" fill="#EF4444"/></svg>
            {{ 'Domain is required' | translate }}
          </div>
          <div class="validation-error" *ngIf="customDomainForm.get('domain')?.hasError('pattern')">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#EF4444" stroke-width="1.1"/><path d="M6 3.5V6.5" stroke="#EF4444" stroke-width="1.1" stroke-linecap="round"/><circle cx="6" cy="8.5" r="0.5" fill="#EF4444"/></svg>
            {{ 'Invalid domain format' | translate }}
          </div>
        </div>

        <!-- DNS Instructions -->
        <div class="dns-instructions"
             *ngIf="domainSetupDone && dnsInstructions.length > 0 && !customDomainVerified">
          <div class="dns-header">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="6.25" stroke="#0284C7" stroke-width="1.1"/>
              <path d="M7.5 6.5V10.5" stroke="#0284C7" stroke-width="1.2" stroke-linecap="round"/>
              <circle cx="7.5" cy="4.75" r="0.6" fill="#0284C7"/>
            </svg>
            <h3>{{ 'DNS Configuration Required' | translate }}</h3>
          </div>
          <p>{{ 'Go to your domain registrar and add the following DNS records' | translate }}:</p>
          <div class="dns-records">
            <div class="dns-record" *ngFor="let record of dnsInstructions">
              <div class="dns-record-row">
                <div class="dns-fields">
                  <div class="dns-field">
                    <span class="dns-label">{{ 'Type' | translate }}</span>
                    <code>{{ record.type }}</code>
                  </div>
                  <div class="dns-field">
                    <span class="dns-label">{{ 'Host' | translate }}</span>
                    <code>{{ record.host }}</code>
                  </div>
                  <div class="dns-field dns-field-grow">
                    <span class="dns-label">{{ 'Value' | translate }}</span>
                    <code>{{ record.value }}</code>
                  </div>
                </div>
                <button class="btn-copy" (click)="copyToClipboard(record.value)" [title]="'Copy' | translate">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <rect x="4" y="4" width="7.5" height="7.5" rx="1" stroke="#8A8A8A" stroke-width="1.1"/>
                    <path d="M2 9V2.5C2 2.22386 2.22386 2 2.5 2H9" stroke="#8A8A8A" stroke-width="1.1" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <p class="dns-note">
            {{ 'DNS changes can take up to 24-48 hours to propagate. Once updated, click "Check Status" below.' | translate }}
          </p>
        </div>

        <!-- Domain status messages -->
        <div class="status-message status-pending"
             *ngIf="domainSetupDone && domainStatus === 'pending' && !customDomainVerified">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5.75" stroke="#B45309" stroke-width="1.1"/>
            <path d="M7 4.5V7L9 8.5" stroke="#B45309" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ 'Domain is not yet pointing to our server. Please update your DNS records.' | translate }}
        </div>

        <div class="status-message status-verified" *ngIf="customDomainVerified">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5.75" stroke="#1D4ED8" stroke-width="1.1"/>
            <path d="M4.5 7L6 8.5L9.5 5" stroke="#1D4ED8" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ 'Domain is active and verified! You can now save it.' | translate }}
        </div>

        <!-- Actions (premium only) -->
        <div class="section-actions" *ngIf="canUseCustomDomain">
          <button
            class="btn-secondary"
            (click)="verifyCustomDomain()"
            [disabled]="customDomainForm.invalid || verifyingDomain">
            <div class="spinner spinner-primary" *ngIf="verifyingDomain"></div>
            <span>{{ verifyingDomain ? ('Verifying...' | translate) : ('Verify Domain' | translate) }}</span>
          </button>

          <button
            *ngIf="domainSetupDone && !customDomainVerified"
            class="btn-secondary"
            (click)="checkDomainStatus()"
            [disabled]="customDomainForm.invalid || checkingDomainStatus">
            <div class="spinner spinner-primary" *ngIf="checkingDomainStatus"></div>
            <span>{{ checkingDomainStatus ? ('Checking...' | translate) : ('Check Status' | translate) }}</span>
          </button>

          <button
            class="btn-primary"
            (click)="saveCustomDomain()"
            [disabled]="customDomainForm.invalid || !customDomainVerified || savingCustomDomain">
            <div class="spinner spinner-white" *ngIf="savingCustomDomain"></div>
            <span>{{ savingCustomDomain ? ('Saving...' | translate) : ('Active Custom Domain' | translate) }}</span>
          </button>
        </div>

      </form>
    </div>
  </div>

  <!-- ─── Help Section ─── -->
  <div class="help-section">
    <h3>{{ 'Need help?' | translate }}</h3>
    <div class="help-grid">
      <div class="container22">
        <div class="help-item">
          <strong>{{ 'Subdomain' | translate }} :</strong>
          {{ 'Free, customizable, and instantly available. Must be unique across our platform.' | translate }}
        </div>
        <div class="help-item">
          <strong>{{ 'SSL / HTTPS' | translate }} :</strong>
          {{ 'All domains on Stacks are automatically secured with SSL certificates at no extra cost.' | translate }}
        </div>
      </div>
      <div class="container22">
        <div class="help-item">
          <strong>{{ 'DNS Propagation' | translate }} :</strong>
          {{ 'Changes to custom domains can take up to 24-48 hours to reflect worldwide.' | translate }}
        </div>
        <div class="help-item">
          <strong>{{ 'Custom Domain' | translate }} :</strong>
          {{ 'Use your own branding. Requires a professional subscription and DNS configuration.' | translate }}
        </div>
      </div>
    </div>
  </div>

</div>
